// ── State machine phases ──────────────────────────────────────────────────────
//
// idle ──play()──► overview ──► driving ──► arriving ──► viewing ──► driving (next)
//                                 ↕                                    ↓ (last)
//                              paused                              complete
// Any phase ──stop()──► idle

export type TourRoutePhase =
  | "idle"
  | "overview"
  | "driving"
  | "arriving"
  | "viewing"
  | "paused"
  | "complete";

// ── Settings ──────────────────────────────────────────────────────────────────

export type AnimationSpeed = "slow" | "average" | "fast";
export type CameraMode     = "follow" | "cinematic" | "overview";
export type AutoAdvance    = "off" | 5 | 10 | 30;
export type VehicleStyle   = "car" | "walking" | "bike";
export type TourTravelMode = "driving" | "walking" | "bicycling" | "transit";

export interface TourRouteSettings {
  enabled:       boolean;
  showRouting:   boolean;        // animate car between photos (on by default)
  travelMode:    TourTravelMode; // affects camera zoom + vehicle animation speed
  speed:         AnimationSpeed;
  cameraMode:    CameraMode;
  autoAdvance:   AutoAdvance;
  vehicleStyle:  VehicleStyle;
  /** Seconds the full-track overview is held at tour start before zooming into waypoint 1. 5–30. */
  overviewDurationSec: number;
}

export const DEFAULT_TOUR_ROUTE_SETTINGS: TourRouteSettings = {
  enabled:      true,
  showRouting:  true,
  travelMode:   "driving",
  speed:        "average",
  cameraMode:   "cinematic",
  autoAdvance:  10,
  vehicleStyle: "car",
  overviewDurationSec: 5,
};

export const SETTINGS_STORAGE_KEY = "tour_route_settings";

/**
 * Target wall-clock seconds to complete the ENTIRE route at each speed level.
 * The actual per-frame speed = totalRouteLength / (targetSeconds * EASE_AVERAGE_FACTOR).
 */
export const TARGET_DURATION_SEC: Record<AnimationSpeed, number> = {
  slow:    60, // 1 minute
  average: 30, // 30 seconds
  fast:     8, // 8 seconds
};

/**
 * Average value of the ease multiplier f(t) = 0.3 + 0.7·sin(π·t) over [0,1].
 * = 0.3 + 0.7·(2/π) ≈ 0.746
 * Used to scale baseSpeedMps so the total travel time equals the target duration.
 */
export const EASE_AVERAGE_FACTOR = 0.3 + 0.7 * (2 / Math.PI);

// ── Per-segment route data ────────────────────────────────────────────────────

/**
 * One slice of the overall route geometry between waypoint[i] and waypoint[i+1].
 * `coords` is in GeoJSON order [lng, lat].
 */
export interface RouteSegment {
  fromWaypointIndex: number;
  toWaypointIndex:   number;
  coords:            [number, number][]; // [lng, lat]
  lengthMeters:      number;
  startMeters:       number;             // cumulative from route origin
  endMeters:         number;
  cumulativeMeters:  number[];           // parallel array to coords
}

// ── Animation frame state ─────────────────────────────────────────────────────

export interface VehiclePosition {
  lng:     number;
  lat:     number;
  /** Degrees clockwise from north — used to rotate the vehicle marker. */
  bearing: number;
}

export interface CameraParams {
  center:   [number, number];
  zoom:     number;
  pitch:    number;
  bearing:  number;
  /** Always 0 — smoothness comes from IIR filters, not MapLibre animation. */
  duration: number;
}

// ── useReducer state & actions ────────────────────────────────────────────────

export interface TourRouteState {
  phase:                TourRoutePhase;
  pausedFrom:           TourRoutePhase | null; // phase we were in before pausing
  currentSegmentIndex:  number;
  metersIntoSegment:    number;
  lastTimestamp:        number;
  carPosition:          VehiclePosition | null;
}

export const INITIAL_TOUR_ROUTE_STATE: TourRouteState = {
  phase:               "idle",
  pausedFrom:          null,
  currentSegmentIndex: 0,
  metersIntoSegment:   0,
  lastTimestamp:       0,
  carPosition:         null,
};

export type TourRouteAction =
  | { type: "play" }
  | { type: "overview_done" }
  | { type: "overview_done_at_first" }
  | { type: "tick"; position: VehiclePosition; metersIntoSegment: number; timestamp: number }
  | { type: "arrive" }
  | { type: "viewing_start" }
  | { type: "viewing_end"; hasNext: boolean }
  | { type: "next_segment" }
  | { type: "pause" }
  | { type: "resume"; timestamp: number }
  | { type: "skip"; hasNext: boolean }
  | { type: "stop" }
  | { type: "complete" };
